import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Descriptions,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  message,
} from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import {
  getPushNotificationHistory,
  getPushNotificationRecipients,
  retryPushNotification,
} from '../../hooks/settings';
import { getUsersDashboard } from '../../hooks/users';

const { RangePicker } = DatePicker;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const BackButton = styled(Button)`
  &,
  &:focus {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
  }
  &:hover {
    color: ${({ theme }) => theme.primary};
    border-color: ${({ theme }) => theme.primary};
    opacity: 0.8;
  }
`;

const RecipientTabs = styled(Tabs)`
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.primary};
  }
  .ant-tabs-tab:hover {
    color: ${({ theme }) => theme.primary};
  }
  .ant-tabs-ink-bar {
    background: ${({ theme }) => theme.primary};
  }
`;

const STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pendente', color: 'blue' },
  { value: 'SENT', label: 'Enviado', color: 'green' },
  { value: 'PARTIAL_FAILURE', label: 'Parcial', color: 'orange' },
  { value: 'FAILED', label: 'Falhou', color: 'red' },
];

const TARGET_TYPE_OPTIONS = [
  { value: 'TENANT', label: 'Tenant inteiro' },
  { value: 'USERS', label: 'Por usuário' },
  { value: 'REGION', label: 'Por região' },
];

const SOURCE_OPTIONS = [
  { value: 'MANUAL', label: 'Manual' },
  { value: 'TRANSACTION_STARTED', label: 'Recarga iniciada' },
  { value: 'TRANSACTION_FINISHED', label: 'Recarga concluída' },
  { value: 'IDLE_CONNECTOR', label: 'Conector ocioso' },
  { value: 'OCPP_EVENT', label: 'Evento OCPP' },
];

const RECIPIENT_STATUS_TAG = {
  PENDING: { color: 'blue', label: 'Pendente' },
  SENT: { color: 'green', label: 'Enviado' },
  FAILED: { color: 'red', label: 'Falhou' },
};

const REGION_LABELS = {
  NORTE: 'Norte',
  NORDESTE: 'Nordeste',
  CENTRO_OESTE: 'Centro-Oeste',
  SUDESTE: 'Sudeste',
  SUL: 'Sul',
};

const parseRegionFilter = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const renderTagList = (items) => (
  <Space size={[4, 4]} wrap>
    {items.map((item, i) => (
      <Tag key={`${item}-${i}`}>{item}</Tag>
    ))}
  </Space>
);

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return String(iso);
  }
};

const PushNotificationHistoryTab = ({ active = true }) => {
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedPush, setSelectedPush] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsFilter, setRecipientsFilter] = useState('all');
  const [resending, setResending] = useState(false);
  // Recipient filter — search-as-you-type, server-driven (no full list preload).
  const [recipientSearchQuery, setRecipientSearchQuery] = useState('');
  const [recipientSearchResults, setRecipientSearchResults] = useState([]);
  const [recipientSearching, setRecipientSearching] = useState(false);
  const [recipientLabelByPk, setRecipientLabelByPk] = useState({});

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPushNotificationHistory({ ...filters, page, size: pageSize });
      setResults(data?.results || []);
      setCount(data?.count || 0);
    } catch {
      message.error(T('Erro ao carregar histórico'));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Refetch when tab is activated to surface fresh data after sends.
  useEffect(() => {
    if (active) fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Recipient filter search — debounced server-side query by name/email/cpf.
  useEffect(() => {
    if (!recipientSearchQuery || recipientSearchQuery.length < 2) {
      setRecipientSearchResults([]);
      return undefined;
    }
    let cancelled = false;
    setRecipientSearching(true);
    const timer = setTimeout(() => {
      getUsersDashboard(true, 1, 20, recipientSearchQuery)
        .then((data) => {
          if (cancelled) return;
          setRecipientSearchResults(data?.userDashboardList || []);
        })
        .catch(() => {})
        .finally(() => !cancelled && setRecipientSearching(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [recipientSearchQuery]);

  const fetchRecipients = useCallback(async (pushPk) => {
    setRecipientsLoading(true);
    try {
      // Max 500 users per push (backend cap), so single page covers all.
      const data = await getPushNotificationRecipients(pushPk, 1, 500);
      setRecipients(data?.results || []);
    } catch {
      message.error(T('Erro ao carregar destinatários'));
    } finally {
      setRecipientsLoading(false);
    }
  }, []);

  const openDrawer = (record) => {
    setSelectedPush(record);
    setRecipientsFilter('all');
    if (record.targetType !== 'TENANT') {
      fetchRecipients(record.pushNotificationPk);
    } else {
      setRecipients([]);
    }
  };

  const closeDrawer = () => {
    setSelectedPush(null);
    setRecipients([]);
    setRecipientsFilter('all');
  };

  const filteredRecipients = useMemo(() => {
    if (recipientsFilter === 'all') return recipients;
    if (recipientsFilter === 'sent') return recipients.filter((r) => r.status === 'SENT');
    if (recipientsFilter === 'failed') return recipients.filter((r) => r.status === 'FAILED');
    return recipients;
  }, [recipients, recipientsFilter]);

  const failedRecipients = useMemo(
    () => recipients.filter((r) => r.status === 'FAILED'),
    [recipients],
  );

  const sentCount = recipients.filter((r) => r.status === 'SENT').length;
  const failedCount = failedRecipients.length;

  const resendUsers = async (userPks) => {
    if (!selectedPush || userPks.length === 0) return;
    setResending(true);
    try {
      const data = await retryPushNotification(selectedPush.pushNotificationPk, userPks);
      if (data?.type === 'success') {
        message.success(T('Reenvio concluído.'));
        await fetchHistory();
        if (selectedPush) await fetchRecipients(selectedPush.pushNotificationPk);
      } else {
        message.error(T('Falha ao reenviar.'));
      }
    } catch {
      message.error(T('Falha ao reenviar.'));
    } finally {
      setResending(false);
    }
  };

  const confirmResend = (userPks, label) => {
    Modal.confirm({
      title: T('Reenviar push?'),
      content: `${label}. ${T('O envio será refeito atualizando este mesmo registro.')}`,
      okText: T('Reenviar'),
      cancelText: T('Cancelar'),
      onOk: () => resendUsers(userPks),
    });
  };

  const updateFilter = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  };

  const columns = [
    {
      title: T('Data'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: formatDateTime,
      width: 170,
    },
    {
      title: T('Origem'),
      dataIndex: 'source',
      key: 'source',
      render: (v) => SOURCE_OPTIONS.find((o) => o.value === v)?.label || v || '—',
      width: 160,
    },
    {
      title: T('Tipo'),
      dataIndex: 'targetType',
      key: 'targetType',
      render: (v) => TARGET_TYPE_OPTIONS.find((o) => o.value === v)?.label || v,
      width: 140,
    },
    {
      title: T('Audiência'),
      dataIndex: 'audienceSize',
      key: 'audienceSize',
      render: (v) => (v == null ? '—' : v),
      width: 100,
    },
    {
      title: T('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (v) => {
        const o = STATUS_OPTIONS.find((x) => x.value === v);
        return o ? <Tag color={o.color}>{o.label}</Tag> : v;
      },
      width: 110,
    },
    {
      title: T('Sucesso / Falha'),
      key: 'sf',
      render: (_, r) =>
        r.audienceSize == null
          ? '—'
          : `${r.successCount ?? 0} / ${r.failureCount ?? 0}`,
      width: 130,
    },
    {
      title: T('Enviado por'),
      key: 'sentBy',
      render: (_, r) => r.createdUserName || r.createdUserEmail || T('Sistema'),
      ellipsis: true,
    },
    {
      title: T('Título'),
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
  ];

  const recipientColumns = [
    {
      title: T('Usuário'),
      dataIndex: 'userName',
      key: 'userName',
      render: (v, r) => v || r.userEmail || r.userPk,
    },
    {
      title: T('E-mail'),
      dataIndex: 'userEmail',
      key: 'userEmail',
      ellipsis: true,
    },
    {
      title: T('Status'),
      dataIndex: 'status',
      key: 'status',
      render: (v) => {
        const o = RECIPIENT_STATUS_TAG[v];
        return o ? <Tag color={o.color}>{o.label}</Tag> : v;
      },
      width: 110,
    },
    {
      title: T('Enviado em'),
      dataIndex: 'sentAt',
      key: 'sentAt',
      render: formatDateTime,
      width: 170,
    },
    {
      title: T('Erro'),
      dataIndex: 'errorCode',
      key: 'errorCode',
      ellipsis: true,
    },
    {
      title: T('Ação'),
      key: 'action',
      width: 110,
      render: (_, r) =>
        r.status === 'FAILED' ? (
          <Button
            size='small'
            type='link'
            disabled={resending}
            onClick={() =>
              confirmResend(
                [r.userPk],
                `${T('Destinatário')}: ${r.userName || r.userEmail}`,
              )
            }
          >
            {T('Reenviar')}
          </Button>
        ) : null,
    },
  ];

  if (selectedPush) {
    const statusLabel =
      STATUS_OPTIONS.find((o) => o.value === selectedPush.status)?.label || selectedPush.status;
    const statusColor =
      STATUS_OPTIONS.find((o) => o.value === selectedPush.status)?.color || 'default';
    const targetLabel =
      TARGET_TYPE_OPTIONS.find((o) => o.value === selectedPush.targetType)?.label ||
      selectedPush.targetType;
    const sourceLabel =
      SOURCE_OPTIONS.find((o) => o.value === selectedPush.source)?.label || selectedPush.source;

    return (
      <Wrapper>
        <Space size='middle' align='center'>
          <BackButton
            icon={<ArrowLeftOutlined />}
            onClick={closeDrawer}
          >
            {T('Voltar')}
          </BackButton>
          <h2 style={{ margin: 0 }}>
            {T('Push')} #{selectedPush.pushNotificationPk}
          </h2>
          <Tag color={statusColor}>{statusLabel}</Tag>
        </Space>

        <Descriptions
          bordered
          column={{ xs: 1, sm: 2, md: 3 }}
          size='small'
        >
          <Descriptions.Item label={T('Tipo')}>{targetLabel}</Descriptions.Item>
          <Descriptions.Item label={T('Origem')}>
            {sourceLabel}
            {selectedPush.contextRef ? ` (${selectedPush.contextRef})` : ''}
          </Descriptions.Item>
          <Descriptions.Item label={T('Audiência')}>
            {selectedPush.audienceSize ?? '—'}
          </Descriptions.Item>
          <Descriptions.Item label={T('Enviado por')}>
            {selectedPush.createdUserName ||
              selectedPush.createdUserEmail ||
              T('Sistema')}
          </Descriptions.Item>
          <Descriptions.Item label={T('Iniciado em')}>
            {formatDateTime(selectedPush.dateCreated)}
          </Descriptions.Item>
          <Descriptions.Item label={T('Concluído em')}>
            {formatDateTime(selectedPush.finishedAt)}
          </Descriptions.Item>
          <Descriptions.Item label={T('Título')} span={3}>
            {selectedPush.subject || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={T('Mensagem')} span={3}>
            {selectedPush.message}
          </Descriptions.Item>
          {selectedPush.targetType === 'REGION' && (() => {
            const f = parseRegionFilter(selectedPush.regionFilter);
            if (!f) return null;
            const items = [];
            if (f.regions && f.regions.length > 0) {
              items.push(
                <Descriptions.Item key='r-regions' label={T('Regiões')} span={3}>
                  {renderTagList(f.regions.map((r) => REGION_LABELS[r] || r))}
                </Descriptions.Item>,
              );
            }
            if (f.states && f.states.length > 0) {
              items.push(
                <Descriptions.Item key='r-states' label={T('Estados')} span={3}>
                  {renderTagList(f.states)}
                </Descriptions.Item>,
              );
            }
            if (f.cities && f.cities.length > 0) {
              items.push(
                <Descriptions.Item key='r-cities' label={T('Cidades')} span={3}>
                  {renderTagList(f.cities)}
                </Descriptions.Item>,
              );
            }
            if (f.countries && f.countries.length > 0) {
              items.push(
                <Descriptions.Item key='r-countries' label={T('Países')} span={3}>
                  {renderTagList(f.countries)}
                </Descriptions.Item>,
              );
            }
            return items;
          })()}
          {selectedPush.failureReason && (
            <Descriptions.Item label={T('Motivo da falha')} span={3}>
              <span style={{ color: 'red' }}>{selectedPush.failureReason}</span>
            </Descriptions.Item>
          )}
        </Descriptions>

        {selectedPush.targetType === 'TENANT' ? (
          <div style={{ color: '#888', padding: '16px 0' }}>
            {T('Envio para o tenant inteiro — destinatários não enumerados.')}
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Space size='middle'>
                <Tag color='green' style={{ fontSize: 14, padding: '4px 8px' }}>
                  ✅ {sentCount} {T('enviados')}
                </Tag>
                <Tag color='red' style={{ fontSize: 14, padding: '4px 8px' }}>
                  ❌ {failedCount} {T('falharam')}
                </Tag>
              </Space>
              {failedCount > 0 && (
                <Button
                  type='primary'
                  danger
                  loading={resending}
                  onClick={() =>
                    confirmResend(
                      failedRecipients.map((r) => r.userPk),
                      `${failedCount} ${T('destinatários falhados')}`,
                    )
                  }
                >
                  {T('Reenviar falhados')} ({failedCount})
                </Button>
              )}
            </div>

            <RecipientTabs
              activeKey={recipientsFilter}
              onChange={setRecipientsFilter}
            >
              <Tabs.TabPane
                key='all'
                tab={`${T('Todos')} (${recipients.length})`}
              />
              <Tabs.TabPane
                key='sent'
                tab={`${T('Sucesso')} (${sentCount})`}
              />
              <Tabs.TabPane
                key='failed'
                tab={`${T('Falha')} (${failedCount})`}
              />
            </RecipientTabs>

            <Table
              rowKey='userPk'
              columns={recipientColumns}
              dataSource={filteredRecipients}
              loading={recipientsLoading}
              size='small'
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
                showTotal: (total) => `${total} ${T('destinatários')}`,
              }}
            />
          </>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FiltersBar>
        <RangePicker
          showTime
          format='DD/MM/YYYY HH:mm'
          placeholder={[T('De'), T('Até')]}
          onChange={(dates) =>
            updateFilter({
              dateFrom: dates?.[0]?.toISOString(),
              dateTo: dates?.[1]?.toISOString(),
            })
          }
        />
        <Select
          allowClear
          placeholder={T('Status')}
          style={{ minWidth: 160 }}
          onChange={(v) => updateFilter({ status: v })}
          options={STATUS_OPTIONS}
        />
        <Select
          allowClear
          placeholder={T('Tipo')}
          style={{ minWidth: 160 }}
          onChange={(v) => updateFilter({ targetType: v })}
          options={TARGET_TYPE_OPTIONS}
        />
        <Select
          allowClear
          placeholder={T('Origem')}
          style={{ minWidth: 180 }}
          onChange={(v) => updateFilter({ source: v })}
          options={SOURCE_OPTIONS}
        />
        <Select
          allowClear
          showSearch
          loading={recipientSearching}
          placeholder={T('Buscar destinatário (nome, email ou CPF)')}
          style={{ minWidth: 320 }}
          onSearch={setRecipientSearchQuery}
          onChange={(v, option) => {
            updateFilter({ recipientUserPk: v });
            if (v && option?.label) {
              setRecipientLabelByPk((prev) => ({ ...prev, [v]: option.label }));
            }
            setRecipientSearchQuery('');
          }}
          filterOption={false}
          notFoundContent={
            recipientSearching
              ? T('Buscando...')
              : recipientSearchQuery.length < 2
                ? T('Digite ao menos 2 caracteres')
                : T('Nenhum usuário encontrado')
          }
          optionLabelProp='label'
          options={recipientSearchResults.map((u) => ({
            value: u.user_pk,
            label: `${u.user_name || ''} - ${u.email || ''}${u.docNumber ? ' - ' + u.docNumber : ''}`,
          }))}
          value={filters.recipientUserPk ?? undefined}
        />
      </FiltersBar>

      <Table
        rowKey='pushNotificationPk'
        columns={columns}
        dataSource={results}
        loading={loading}
        size='small'
        pagination={{
          current: page,
          pageSize,
          total: count,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
          showTotal: (total) => `${total} ${T('registros')}`,
        }}
        onRow={(record) => ({
          onClick: () => openDrawer(record),
          style: { cursor: 'pointer' },
        })}
      />
    </Wrapper>
  );
};

export default PushNotificationHistoryTab;
